// LoadData 後段の描画制御をまとめる coordinator です。
// 実際の画面依存処理は deps から受け取り、ここでは順序制御に専念します。

export function createEntryVisualPostLoadCoordinator(deps) {
    // Entry Loading 終了待ちの世代番号。保険タイマーが古い世代で誤発火しないよう識別します。
    let finishEntryLoadingGeneration = 0;

    // requestAnimationFrame が使える環境では描画タイミングへ寄せます。
    function runDeferred(callback) {
        if (typeof deps.requestAnimationFrame === "function") {
            deps.requestAnimationFrame(callback);
            return;
        }
        deps.setTimeout(callback, 0);
    }

    // setTimeout / requestAnimationFrame のどちらで積まれたハンドルでも解除します。
    function clearScheduledHandle(timerId) {
        if (deps.isEmpty(timerId)) return;
        if (typeof deps.cancelAnimationFrame === "function") {
            deps.cancelAnimationFrame(timerId);
        }
        deps.clearTimeout(timerId);
    }

    // Entry Loading の終了待ちタイマーが残っている場合だけ解除します。
    function clearFinishTimerIfNeeded() {
        const timerId = deps.getFinishEntryLoadingTimer();
        if (deps.isEmpty(timerId)) return;
        clearScheduledHandle(timerId);
        deps.setFinishEntryLoadingTimer(null);
    }

    // 現在の表示モードで再描画対象となる Grid ID 群を集約します。
    function buildVisibleGridIds(targetType) {
        const visibleSections = deps.resolveVisibleSections(targetType);
        const targetIds = [];

        visibleSections.forEach((section) => {
            const gridSectionPanel = deps.resolveGridSectionPanel(section);
            if (!deps.isEmpty(gridSectionPanel)) {
                const gid = deps.getGridId(gridSectionPanel);
                if (!deps.isEmpty(gid)) targetIds.push(gid);
            }

            const totalPanel = deps.resolveTotalHostPanel(section, gridSectionPanel);
            if (!deps.isEmpty(totalPanel)) {
                const tid = deps.getGridId(totalPanel);
                if (!deps.isEmpty(tid)) targetIds.push(tid);
            }
        });

        return [...new Set(targetIds)];
    }

    // Grid 再描画を分割実行し、長い同期処理で UI を止めないようにします。
    function runVisibleGridRefreshChunks({
        targetType,
        source,
        effectiveIds,
        chunkSize,
        deferAfterWaitRelease
    }) {
        const refreshStartedAt = deps.now();

        const fireChunk = (startIndex = 0, gridTimings = []) => {
            const chunk = effectiveIds.slice(startIndex, startIndex + chunkSize);
            if (chunk.length === 0) {
                deps.logInteractionDiagnostics("scheduleVisibleGridRefresh:fireChunk:abort", {
                    targetType,
                    source,
                    startIndex,
                    reason: "emptyChunk"
                });
                return;
            }

            const nextGridTimings = [...gridTimings];
            deps.logInteractionDiagnostics("scheduleVisibleGridRefresh:fireChunk:start", {
                targetType,
                source,
                startIndex,
                chunkIds: chunk,
                accumulatedGridCount: gridTimings.length
            });

            chunk.forEach((gridId, chunkIndex) => {
                const gridStartedAt = deps.now();
                let mode = "redux";

                // wait 解除直後は API 直反映を優先し、無理なら既存 rerender 経路へ戻します。
                if (deferAfterWaitRelease) {
                    const panelInfo = deps.findGridPanelInfoByGridId(gridId);
                    if (deps.refreshGridPanelByApi(panelInfo)) {
                        mode = "api";
                    } else {
                        deps.rerenderGridById(gridId);
                    }
                } else {
                    deps.rerenderGridById(gridId);
                }

                const elapsedMs = Math.round(deps.now() - gridStartedAt);
                nextGridTimings.push({
                    id: gridId,
                    elapsedMs,
                    mode
                });

                deps.logInteractionDiagnostics("scheduleVisibleGridRefresh:fireChunk:grid", {
                    targetType,
                    source,
                    startIndex,
                    chunkIndex,
                    gridId,
                    elapsedMs,
                    mode
                });
            });

            const nextIndex = startIndex + chunk.length;
            if (nextIndex < effectiveIds.length) {
                deps.logInteractionDiagnostics("scheduleVisibleGridRefresh:fireChunk:next", {
                    targetType,
                    source,
                    startIndex,
                    nextIndex,
                    remainingCount: effectiveIds.length - nextIndex
                });
                runDeferred(() => fireChunk(nextIndex, nextGridTimings));
                return;
            }

            deps.logInteractionDiagnostics("scheduleVisibleGridRefresh:fire", {
                targetType,
                source,
                gridIds: effectiveIds,
                gridTimings: nextGridTimings,
                chunkSize,
                totalMs: Math.round(deps.now() - refreshStartedAt)
            });
        };

        deps.logInteractionDiagnostics("scheduleVisibleGridRefresh:kickoff:start", {
            targetType,
            source,
            gridIds: effectiveIds,
            chunkSize
        });

        runDeferred(() => fireChunk(0, []));
    }

    // 原価 / 洗い表示などの遅延補正を、基本描画が一段落したあとに実行します。
    function finalizeEntryVisualData(code, detailData, data) {
        const startedAt = deps.now();
        deps.setFinalizeEntryVisualTrace({
            code,
            startedAt,
            layoutCount: 0,
            effectCount: 0
        });

        deps.logEntryVisualTrace("finalizeEntryVisualData:start", {
            code,
            detailPanelCount: deps.isEmpty(detailData) ? 0 : Object.keys(detailData).length,
            gridCount: Array.isArray(deps.getGridList()) ? deps.getGridList().length : 0
        }, { forceTrace: true });

        deps.setTimeout(() => {
            const timeoutStartedAt = deps.now();
            deps.logEntryVisualTrace("finalizeEntryVisualData:setTimeout:start", {
                code,
                queueDelayMs: Math.round(timeoutStartedAt - startedAt)
            }, { forceTrace: true });

            const ensureGenkaStartedAt = deps.now();
            deps.ensureGenkaRowsBound();
            const ensureGenkaRowsBoundMs = Math.round(deps.now() - ensureGenkaStartedAt);

            let recalcGenkaGridMs = 0;
            const gridlist = deps.getGridList();
            // 原価 Grid が存在する場合だけ再集計し、不要な全体 rerender を避けます。
            if (Array.isArray(gridlist) && gridlist.length !== 0) {
                const genkaPanel = gridlist.find((g) => deps.isGenkaGridPanelInfo(g));
                if (!deps.isEmpty(genkaPanel)) {
                    const recalcStartedAt = deps.now();
                    deps.recalcGenkaGridAndSummary(genkaPanel, { skipGlobalRerender: true });
                    deps.setGenkaPostLoadRefreshSkip(true);
                    recalcGenkaGridMs = Math.round(deps.now() - recalcStartedAt);
                }
            }

            const ensureWashStartedAt = deps.now();
            deps.ensureWashRowsBound();

            deps.logEntryVisualTrace("finalizeEntryVisualData:setTimeout:done", {
                code,
                ensureGenkaRowsBoundMs,
                recalcGenkaGridMs,
                ensureWashRowsBoundMs: Math.round(deps.now() - ensureWashStartedAt),
                totalElapsedMs: Math.round(deps.now() - startedAt)
            }, { forceTrace: true });
        }, 0);

        deps.logEntryVisualTrace("finalizeEntryVisualData:skipGlobalRerender", {
            code,
            reason: "dispatchBaseEntryData already committed entry fields"
        }, { forceTrace: true });
    }

    // loading 中に保留した表示中 Grid の再描画要求を、wait 解放後に流します。
    function flushPendingVisibleGridRefresh() {
        const targetType = deps.getPendingVisibleGridRefresh();
        if (deps.isEmpty(targetType)) {
            deps.logInteractionDiagnostics("flushPendingVisibleGridRefresh", {
                flushed: false,
                targetType: ""
            });
            return false;
        }

        deps.setPendingVisibleGridRefresh("");
        deps.logInteractionDiagnostics("flushPendingVisibleGridRefresh", {
            flushed: true,
            targetType
        });

        scheduleVisibleGridRefresh(targetType, {
            source: "flushPendingVisibleGridRefresh",
            deferAfterWaitRelease: true,
            chunkSize: 4
        });
        return true;
    }

    // 現在表示中の Grid だけを再描画し、不要な再反映を減らします。
    function scheduleVisibleGridRefresh(targetType = deps.getKakouType(), options = {}) {
        const source = String(deps.DicVal(options, "source", "") ?? "").trim();
        const deferAfterWaitRelease = Boolean(deps.DicVal(options, "deferAfterWaitRelease", false));
        const chunkSize = Math.max(1, Number(deps.DicVal(options, "chunkSize", 4)) || 4);

        // loading 中はすぐ実行せず、最後に 1 件だけ残して wait 解放後に流します。
        if (deps.isEntryLoading()) {
            deps.setPendingVisibleGridRefresh(targetType);
            deps.logInteractionDiagnostics("scheduleVisibleGridRefresh:deferred", {
                targetType,
                source
            });
            return;
        }

        const uniqueIds = buildVisibleGridIds(targetType);
        if (uniqueIds.length === 0) return;

        // wait 解放直後は、画面に残す意味がある Grid だけへ絞ります。
        const effectiveIds = deferAfterWaitRelease
            ? uniqueIds.filter((gridId) => {
                const panelInfo = deps.findGridPanelInfoByGridId(gridId);
                return deps.shouldKeepPostLoadVisibleGridRefresh(panelInfo);
            })
            : uniqueIds;
        if (effectiveIds.length === 0) return;

        deps.logInteractionDiagnostics("scheduleVisibleGridRefresh:scheduled", {
            targetType,
            source,
            gridIds: effectiveIds,
            chunkSize,
            deferAfterWaitRelease
        });

        const kickoff = () => {
            runVisibleGridRefreshChunks({
                targetType,
                source,
                effectiveIds,
                chunkSize,
                deferAfterWaitRelease
            });
        };

        // wait 解放直後だけは 1 ティック遅らせ、既存 DOM / API の安定化を待ちます。
        if (deferAfterWaitRelease) {
            deps.setTimeout(kickoff, 0);
            return;
        }

        if (typeof deps.requestAnimationFrame === "function") {
            deps.requestAnimationFrame(() => deps.requestAnimationFrame(kickoff));
            return;
        }

        deps.setTimeout(kickoff, 0);
    }

    // Entry Loading の完了処理を 1 か所に寄せ、wait 解放と後続処理を揃えます。
    function finishEntryLoading() {
        clearFinishTimerIfNeeded();

        const timing = deps.getEntryLoadTiming();
        if (!deps.isEmpty(timing)) {
            const finishAt = deps.now();
            deps.logEntryLoadTiming({
                code: timing.code,
                stage: "finish",
                requestMs: Math.round((timing.responseAt ?? finishAt) - timing.requestStartAt),
                applyMs: Math.round((timing.applyEndAt ?? finishAt) - (timing.applyStartAt ?? timing.requestStartAt)),
                paintWaitMs: Math.round(finishAt - (timing.applyEndAt ?? finishAt)),
                totalMs: Math.round(finishAt - timing.requestStartAt),
                stepMs: timing.stepMs ?? {}
            });
        }

        const finishedCode = !deps.isEmpty(timing)
            ? String(timing.code ?? "")
            : deps.getEntryVisualTraceCode();

        deps.activateEntryVisualTraceWindow(3000);
        deps.logEntryVisualTrace("finishEntryLoading:before", { code: finishedCode });

        deps.clearEntryLoadTiming();
        deps.setEntryLoading(false);

        let waitOffMs = 0;
        const waitOffStartedAt = deps.now();
        deps.waitOff();
        waitOffMs = Math.round(deps.now() - waitOffStartedAt);

        const flushStartedAt = deps.now();
        const flushedGridRefresh = flushPendingVisibleGridRefresh();
        const flushPendingVisibleGridRefreshMs = Math.round(deps.now() - flushStartedAt);

        deps.setLastEntryFinishLoadingAt(deps.now());

        deps.logEntryVisualTrace("finishEntryLoading:after", {
            code: finishedCode,
            waitOffMs,
            flushedGridRefresh,
            flushPendingVisibleGridRefreshMs
        });

        deps.scheduleDeferredColorwayMatricesLoad(finishedCode);
        deps.schedulePostFinishEntryVisualTrace(finishedCode);
        deps.schedulePostFinishInteractionDiagnostics(finishedCode);
    }

    // 2 フレーム待ってから loading を外し、描画コミットの取りこぼしを減らします。
    function scheduleFinishEntryLoading() {
        if (!deps.isEntryLoading()) return;
        if (!deps.isEmpty(deps.getFinishEntryLoadingTimer())) return;

        deps.logEntryVisualTrace("scheduleFinishEntryLoading", {
            pendingStepMs: deps.DicVal(deps.getEntryLoadTiming(), "stepMs", {})
        });

        finishEntryLoadingGeneration += 1;
        const generation = finishEntryLoadingGeneration;

        const finish = () => {
            deps.setFinishEntryLoadingTimer(null);
            if (!deps.isEntryLoading()) return;
            finishEntryLoading();
        };

        if (typeof deps.requestAnimationFrame === "function") {
            const raf1 = deps.requestAnimationFrame(() => {
                if (typeof deps.requestAnimationFrame === "function") {
                    const raf2 = deps.requestAnimationFrame(finish);
                    deps.setFinishEntryLoadingTimer(raf2);
                } else {
                    finish();
                }
            });
            deps.setFinishEntryLoadingTimer(raf1);
            // 非アクティブタブでは rAF が発火せず loading が解除されないため、保険のタイマーで終了させる。
            // 世代が進んでいる（再スケジュール済み）か、キャンセル済み（タイマーなし）の場合は何もしない。
            deps.setTimeout(() => {
                if (generation !== finishEntryLoadingGeneration) return;
                if (deps.isEmpty(deps.getFinishEntryLoadingTimer())) return;
                finish();
            }, 1000);
            return;
        }

        deps.setFinishEntryLoadingTimer(deps.setTimeout(finish, 0));
    }

    return {
        finalizeEntryVisualData,
        finishEntryLoading,
        scheduleFinishEntryLoading,
        flushPendingVisibleGridRefresh,
        scheduleVisibleGridRefresh
    };
}
